/**
 * Plan diff (spec §15): "a diff view showing exactly which inputs differ
 * between scenarios. Users lose track, and an untracked difference makes the
 * comparison meaningless." Structural deep-compare with readable paths.
 */
import type { Plan } from '../model/types';

export interface PlanDifference {
  /** Dotted path, e.g. "household.people[0].retirementAge". */
  path: string;
  a: unknown;
  b: unknown;
}

const SKIP_KEYS = new Set(['id', 'schemaVersion']);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function walk(a: unknown, b: unknown, path: string, out: PlanDifference[]): void {
  if (Object.is(a, b)) return;

  if (Array.isArray(a) && Array.isArray(b)) {
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      walk(a[i], b[i], `${path}[${i}]`, out);
    }
    return;
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (SKIP_KEYS.has(key)) continue;
      walk(a[key], b[key], path ? `${path}.${key}` : key, out);
    }
    return;
  }

  // Leaf mismatch (or type mismatch / added / removed).
  out.push({ path, a, b });
}

/** All input-level differences between two plans (derived values never live in Plan). */
export function diffPlans(a: Plan, b: Plan): PlanDifference[] {
  const out: PlanDifference[] = [];
  walk(a as unknown, b as unknown, '', out);
  return out;
}

/** Human-ish rendering of a diff value. */
export function fmtDiffValue(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'none';
  if (typeof v === 'number') {
    return Math.abs(v) >= 1000 ? v.toLocaleString('en-US') : String(Math.round(v * 1000) / 1000);
  }
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}
